/**
 * The invite A/B test switch, `?invite=a` or `?invite=b`, kept for the tab.
 *
 * main.ts calls captureInviteOverride() before Angular boots, because the
 * router (and stripAppRedirectToken) rewrite the URL long before the invite
 * dialog opens. The query can sit before the # or inside it; any other
 * `?invite=` value clears the kept one. Storing it is harmless everywhere:
 * InviteShareComponent only honours it on a build with
 * `inviteAb.allowOverride` (the test build).
 */
import { environment } from 'src/environments/environment';

const KEY = 'invite_ab_override';

/**
 * The sandbox sites. They build with the production configuration, which
 * switches `inviteAb.allowOverride` off, so without naming them here there is
 * nowhere to look at version B before it reaches real hosts.
 */
const SANDBOX_HOSTS = ['mypicaggo.web.app', 'test-picaggo.web.app'];

/**
 * Whether `?invite=a|b` may pick a version here. The build flag covers a local
 * dev server; the sandbox hosts are allowed by name. events.picaggo.com never
 * is, whatever the build config says — a test build deployed there by mistake
 * must not let a real host pick their own arm and skew the result.
 */
export function inviteOverrideAllowed(): boolean {
  try {
    const host = window.location.hostname;
    if (host === 'events.picaggo.com') return false;
    return !!environment.inviteAb?.allowOverride || SANDBOX_HOSTS.includes(host);
  } catch {
    return false;
  }
}

export function captureInviteOverride(): void {
  try {
    const hash = window.location.hash || '';
    const queries = [window.location.search, hash.includes('?') ? hash.slice(hash.indexOf('?')) : ''];
    for (const q of queries) {
      const v = new URLSearchParams(q).get('invite');
      if (v === null) continue;
      if (v === 'a' || v === 'b') {
        sessionStorage.setItem(KEY, v);
      } else {
        sessionStorage.removeItem(KEY);
      }
      return;
    }
  } catch {
    // Storage blocked: the switch just doesn't stick.
  }
}

export function keptInviteOverride(): 'a' | 'b' | null {
  try {
    const v = sessionStorage.getItem(KEY);
    return v === 'a' || v === 'b' ? v : null;
  } catch {
    return null;
  }
}
